import React from 'react';

/**
 * Moyens de paiement acceptés, affichés sous les grilles tarifaires.
 *
 * Pourquoi les nommer explicitement : la pénétration de la carte bancaire est
 * marginale dans l'UEMOA, et une grille de prix qui n'annonce rien laisse
 * supposer « carte obligatoire » — un motif d'abandon avant même le clic. La
 * mention « PayDunia », seule, ne parle qu'aux initiés.
 *
 * ⚠️ METHODS doit refléter les canaux réellement activés sur le compte marchand
 * PayDunia : l'intégration passe par le Checkout Invoice standard
 * (api/app/routers/payments.py), qui n'impose aucune liste côté code — c'est la
 * configuration du compte qui décide de ce que le client voit à l'arrivée.
 * Annoncer ici un canal désactivé se paierait en abandon au moment de payer.
 */
const METHODS: { label: string; color: string }[] = [
    { label: 'Orange Money', color: '#FF7900' },
    { label: 'Wave', color: '#1DC8FF' },
    { label: 'MTN MoMo', color: '#FFCC00' },
    { label: 'Moov Money', color: '#0057B8' },
    { label: 'Free Money', color: '#E30613' },
];

export interface PaymentMethodsProps {
    /** Marge haute, pour s'adapter à l'espacement de l'écran hôte. */
    marginTop?: number | string;
    /** 'landing' hérite des tokens --lp-* ; 'app' de ceux de l'application. */
    theme?: 'landing' | 'app';
}

const PaymentMethods: React.FC<PaymentMethodsProps> = ({ marginTop = 32, theme = 'landing' }) => {
    const muted = theme === 'landing' ? 'var(--lp-text-muted, #9CA3AF)' : 'var(--text-muted, #9CA3AF)';
    const sub = theme === 'landing' ? 'var(--lp-text-sub, #4B5563)' : 'var(--text-main, #4B5563)';
    const border = theme === 'landing' ? 'var(--lp-border, #E8E4F0)' : 'var(--border-color, #E8E4F0)';
    const surface = theme === 'landing' ? 'var(--lp-card, #FFFFFF)' : 'var(--bg-box, #FFFFFF)';

    return (
        <div style={{ marginTop, textAlign: 'center' }}>
            <p style={{ margin: '0 0 14px 0', fontSize: '0.875rem', fontWeight: 700, color: sub }}>
                Payez par Mobile Money — ou par carte bancaire
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                {METHODS.map((m) => (
                    <span
                        key={m.label}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 7,
                            padding: '6px 14px',
                            borderRadius: 100,
                            border: `1px solid ${border}`,
                            background: surface,
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: sub,
                        }}
                    >
                        <span
                            aria-hidden="true"
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: m.color,
                                flexShrink: 0,
                            }}
                        />
                        {m.label}
                    </span>
                ))}
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '6px 14px',
                        borderRadius: 100,
                        border: `1px dashed ${border}`,
                        background: surface,
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: muted,
                    }}
                >
                    Visa · Mastercard
                </span>
            </div>

            <p style={{ margin: '14px 0 0 0', fontSize: '0.75rem', color: muted }}>
                🔒 Paiement sécurisé via PayDunia — aucune coordonnée bancaire n'est stockée par KaneaBourse
            </p>
        </div>
    );
};

export default PaymentMethods;
